import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import {
    Account,
    GroupRtbs,
    Model,
    ModelComponent,
    ModelFractionalSharePolicy,
    OrderTransaction,
    Portfolio,
    PositionType,
    RebalanceRecommendationItem,
} from '@orbis-systems/orbis-api-middleware';
import { MiddlewareRebalanceDataDtoFractionalSharePolicyEnum } from '@orbis-systems/orbis-api-clients/middleware';
import { queryOptions } from '@tanstack/react-query';

import { buildModelPortfolioWithBreakdownFromCache } from './models-query-utils';
import { orbis, orbisMiddleware } from '../api';

export interface ModelMemberRealtimeBalancesQueryParams {
    modelId: 'models' | string;
}
export interface ModelListingQueryParams {
    loadQuotes: boolean;
    loadAccounts: boolean;
}

export interface ModelPortfolioWithBreakdownQueryParams {
    modelId: number;
    currency: string;
    lastUpdatedAt: number;
}
export interface ModelPortfolioOptions {
    combined?: boolean;
    currency?: string;
    symbol?: string;
    positionType?: PositionType;
}

export interface ModelPortfolioQueryParams {
    modelId: string;
    currency?: string;
    /**
     * Should load quotes or not.
     * @example "false"
     */
    loadQuotes?: boolean;
    /**
     * Whether to return combined portfolio (no accounts, with approximate prices)
     * @example "false"
     */
    combined?: boolean;
    /** @example "false" */
    loadIndustries?: boolean;
    /** @example "false" */
    loadUpgrades?: boolean;
    /** @example "false" */
    loadEarnings?: boolean;
    /**
     * Whether or not to include orders comprising each position's cost
     * @example "false"
     */
    loadCost?: boolean;
    /**
     * Analyze market caps and get `marketCapWeights`. To be used in conjuction with `loadQuotes`
     * @example "false"
     */
    marketCapWeights?: boolean;
}

export interface ModelMemberAccountStatisticsQueryParams {
    modelId: string;
}
export interface ModelMemberAccountsQueryParams {
    modelId: string;
}

export interface ModelMemberAccount {
    account: Account;
    model: Model;
}

export interface AllocationTarget {
    quantity: number;
    account: {
        accountNumber: string;
    };
}

export interface AllocationsParams {
    orders: Array<{
        ourRef: string;
        quantity: number;
    }>;
    targets: Array<AllocationTarget>;
}

export interface ModelSymbolComponent {
    symbol: string;
    percentage: number;
}

export interface ModelCreationParams {
    title: string;
    description: string;
    reserve: number;
    fractionalSharePolicy: MiddlewareRebalanceDataDtoFractionalSharePolicyEnum;
    components: ModelSymbolComponent[];
}

export interface ModelUpdateParams {
    id: number;
    title: string;
    description: string;
    reserve: number;
    fractionalSharePolicy: MiddlewareRebalanceDataDtoFractionalSharePolicyEnum;
}

export interface ModelComponentUpdateParams extends ModelSymbolComponent {
    model: {
        id: number;
    };
}

export interface ModelRealtimeBalanceHistoryParams {
    modelId: string;
    dateFrom: string;
    dateTo: string;
}

export interface ModelRealtimeBalanceHistory {
    date: string;
    balance: number;
    modelId: number;
}

export type MemberRealtimeBalancesResponse = SuccessClientResponse<GroupRtbs[]>;
export type ListingResponse = SuccessClientResponse<Model[]>;
export type PortfolioResponse = SuccessClientResponse<Portfolio>;
export type MemberAccountStatisticsResponse = SuccessClientResponse<Account[]>;
export type ModelMemberAccountsResponse = SuccessClientResponse<ModelMemberAccount[]>;
export type AllocationsResponse = SuccessClientResponse<{
    AllocationRef: string;
}>;
export type ModelRealtimeBalanceHistoryResponse = SuccessClientResponse<ModelRealtimeBalanceHistory[]>;

export interface UnlinkMemberAccountParams {
    modelId: number;
    accountId: number;
}

export interface LinkMemberAccountParams {
    modelId: number;
    accountId: number;
}

export interface ModelRebalanceRecommendationsQueryParams {
    modelId: number;
    targets: Record<string, number>;
    accountIds: number[];
    presets?: {
        accountId: number;
        transaction: OrderTransaction.Buy | OrderTransaction.Sell;
        symbol: string;
        quantity: number;
    }[];
    fractionalSharePolicy?: ModelFractionalSharePolicy;
    fractionalSharePrecision?: number;
}

export interface OrphanedAccount {
    account: Account;
}

export type ModelDeletionResponse = SuccessClientResponse<void>;
export type ModelCreationResponse = SuccessClientResponse<Model>;
export type ModelUpdateResponse = SuccessClientResponse<void>;
export type ModelComponentUpdateResponse = SuccessClientResponse<ModelComponent>;
export type UnlinkMemberAccountResponse = SuccessClientResponse<void>;
export type LinkMemberAccountResponse = SuccessClientResponse<void>;
export type OrphanedAccountsResponse = SuccessClientResponse<OrphanedAccount[]>;

export const modelQueries = {
    all: () => queryOptions({ queryKey: ['models'] }),
    memberRealtimeBalances: (params: ModelMemberRealtimeBalancesQueryParams) =>
        queryOptions<MemberRealtimeBalancesResponse>({
            queryKey: [...modelQueries.all().queryKey, 'memberRealtimeBalances', params],
            queryFn: () => orbis.advisory.models.memberRealtimeBalances(params.modelId),
        }),
    listing: (params?: ModelListingQueryParams) =>
        queryOptions<ListingResponse>({
            queryKey: [...modelQueries.all().queryKey, 'listing', params],
            queryFn: () => orbis.advisory.models.listing(params),
        }),
    modelPortfolioWithBreakdown: (params: ModelPortfolioWithBreakdownQueryParams) => {
        const { modelId, currency, lastUpdatedAt } = params;
        return queryOptions({
            queryKey: [
                ...modelQueries.all().queryKey,
                'modelPortfolioWithBreakdown',
                modelId,
                { currency, lastUpdatedAt },
            ],
            queryFn: buildModelPortfolioWithBreakdownFromCache({ currency, modelId }),
        });
    },
    portfolio: (params: ModelPortfolioQueryParams) => {
        const { modelId, ...rest } = params;
        return queryOptions<PortfolioResponse>({
            queryKey: [...modelQueries.all().queryKey, 'portfolio', modelId, rest],
            queryFn: () => orbis.advisory.models.portfolio(modelId, rest),
        });
    },
    memberAccountStatistics: (params: ModelMemberAccountStatisticsQueryParams) =>
        queryOptions<MemberAccountStatisticsResponse>({
            queryKey: [...modelQueries.all().queryKey, 'memberAccountStatistics', params],
            queryFn: () => orbis.advisory.models.memberAccountStatistics(params.modelId),
        }),
    memberAccounts: (params: ModelMemberAccountsQueryParams) =>
        queryOptions<ModelMemberAccountsResponse>({
            queryKey: [...modelQueries.all().queryKey, 'memberAccounts', params],
            queryFn: () => orbis.advisory.models.memberAccounts(params.modelId),
        }),
    orphanedAccounts: () =>
        queryOptions<OrphanedAccountsResponse>({
            queryKey: [...modelQueries.all().queryKey, 'orphanedAccounts'],
            queryFn: () => orbis.advisory.models.orphanedAccounts(),
        }),
    realtimeBalanceHistory: (params: ModelRealtimeBalanceHistoryParams) =>
        queryOptions<ModelRealtimeBalanceHistoryResponse>({
            queryKey: [...modelQueries.all().queryKey, 'realtimeBalanceHistory', params],
            queryFn: () =>
                orbis.advisory.models.realtimeBalanceHistory(params.modelId, {
                    dateFrom: params.dateFrom,
                    dateTo: params.dateTo,
                }),
        }),
    getRebalanceRecommendations: (params: ModelRebalanceRecommendationsQueryParams) =>
        queryOptions<RebalanceRecommendationItem[]>({
            queryKey: [...modelQueries.all().queryKey, 'getRebalanceRecommendations', params],
            queryFn: () =>
                orbisMiddleware.model.getRebalanceRecommendations(
                    params.modelId,
                    params.targets,
                    params.accountIds,
                    params.presets,
                    params.fractionalSharePolicy,
                    params.fractionalSharePrecision,
                ),
        }),
};

export const modelMutations = {
    allocations: (params: AllocationsParams): Promise<AllocationsResponse> => orbis.advisory.models.allocations(params),
    deletion: (id: number): Promise<ModelDeletionResponse> => orbis.advisory.models.deletion({ id }),
    creation: (params: ModelCreationParams): Promise<ModelCreationResponse> => orbis.advisory.models.creation(params),
    update: (params: ModelUpdateParams): Promise<ModelUpdateResponse> => orbis.advisory.models.update(params),
    componentUpdate: (params: ModelComponentUpdateParams): Promise<ModelComponentUpdateResponse> =>
        orbis.advisory.models.componentUpdate(params),
    unlinkMemberAccount: (params: UnlinkMemberAccountParams): Promise<UnlinkMemberAccountResponse> =>
        orbis.advisory.models.unlinkMemberAccount(params),
    linkMemberAccount: (params: LinkMemberAccountParams): Promise<LinkMemberAccountResponse> =>
        orbis.advisory.models.linkOfMemberAccounts(params),
};
